/**
 * Strategy Options — paper OPTION executor that shadows the Strategy Scanner (S1/S2).
 * It does not change the strategy: it buys an ATM CE/PE on each S1/S2 signal and
 * exits when the parent trade exits, reporting option-premium P&L in rupees.
 */

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

const API = "/api/zerodha";

function confColor(label) {
  return { "A+": "#34d399", A: "#60a5fa", B: "#f59e0b", C: "#f87171" }[label] || "var(--grey-text2)";
}
function ConfBadge({ t }) {
  if (t.confidence == null) return <span style={{ color: "var(--edge)" }}>-</span>;
  const c = confColor(t.confidence_label);
  return <span style={{ padding: "1px 6px", borderRadius: 3, fontSize: 9, fontWeight: 700, background: c + "22", color: c, border: `1px solid ${c}` }}>{t.confidence} {t.confidence_label}</span>;
}

function fmtTime(s) {
  return s ? new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "-";
}

function exportCSV(trades) {
  if (!trades.length) return;
  const h = "Parent,Conf,Dir,Index,OptType,Strike,Symbol,Expiry,Qty,EntryPremium,ExitPremium,Capital,P&L,Result,ExitReason,EntryTime,ExitTime\n";
  const rows = trades.map(t =>
    `${t.parent_strategy},${t.confidence ?? ""}${t.confidence_label || ""},${t.direction},${t.index},${t.opt_type},${t.strike},${t.symbol},${t.expiry},${t.qty},${t.entry_premium},${t.exit_premium ?? ""},${t.capital},${t.pnl || 0},${t.result || "OPEN"},${t.exit_reason || ""},${t.entry_time},${t.exit_time || ""}`
  ).join("\n");
  const blob = new Blob([h + rows], { type: "text/csv" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "strategy-options-trades.csv"; a.click();
}

export default function StrategyOptions() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);

  const poll = async () => {
    try {
      const res = await axios.get(`${API}/strategy-options/status`, { timeout: 10000 });
      if (res.data.success) setStatus(res.data);
    } catch {}
  };
  useEffect(() => { poll(); pollRef.current = setInterval(poll, 10000); return () => clearInterval(pollRef.current); }, []);

  const start = async () => { setLoading(true); try { await axios.post(`${API}/strategy-options/start`); } catch {} setTimeout(poll, 1000); setLoading(false); };
  const stop = async () => { setLoading(true); try { await axios.post(`${API}/strategy-options/stop`); } catch {} setTimeout(poll, 1000); setLoading(false); };
  const clear = async () => { if (!window.confirm("Clear all closed option trades?")) return; try { await axios.post(`${API}/strategy-options/clear`); } catch {} setTimeout(poll, 500); };

  const trades = status?.trades || [];
  const open = trades.filter(t => t.status === "OPEN");
  const closed = trades.filter(t => t.status === "CLOSED");
  const sm = status?.params?.strike_mode || "ATM";

  return (
    <div className="five-ema">
      <div className="dashboard-header">
        <div>
          <h2>Strategy Options <span style={{ fontSize: 12, color: "#a78bfa" }}>(paper, shadows Strategy Scanner)</span></h2>
          <p className="dashboard-subtitle">Buys an {sm} CE/PE on each S1/S2 signal and exits when the parent trade exits — strategy unchanged. P&L in ₹ (premium × lot).</p>
        </div>
        <div className="dashboard-actions">
          <button className={`btn ${status?.active ? "btn-danger" : "btn-primary"}`} onClick={status?.active ? stop : start} disabled={loading}>
            {status?.active ? "Stop Executor" : "Start Executor"}
          </button>
        </div>
      </div>

      {status && (
        <div style={{ display: "flex", gap: 16, padding: "8px 12px", background: status.active ? "var(--tint-pos)" : "var(--panel3)", borderBottom: "1px solid var(--surface3)", fontSize: 12, color: "var(--grey-text)", flexWrap: "wrap", alignItems: "center" }}>
          <span>{status.active ? <span style={{ color: "#34d399" }}>RUNNING</span> : "STOPPED"}</span>
          <span>Checks: {status.scanCount}</span>
          {status.lastScanTime && <span>Last: {new Date(status.lastScanTime).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })}</span>}
          <span>Market: {status.marketOpen ? <span style={{ color: "#34d399" }}>OPEN</span> : <span style={{ color: "#f87171" }}>CLOSED</span>}</span>
          <span>Strike: <strong>{sm}</strong></span>
          <span>Open: <strong>{status.openTrades}</strong></span>
          <span>WR: <strong>{status.winRate}%</strong> ({status.wins}/{status.closed})</span>
          <span style={{ color: "var(--edge)" }}>|</span>
          <span>Booked P&L: <strong className={status.totalPnl >= 0 ? "positive" : "negative"}>{status.totalPnl > 0 ? "+" : ""}₹{status.totalPnl}</strong></span>
        </div>
      )}

      {open.length > 0 && (
        <div style={{ padding: "8px 12px" }}>
          <h4 style={{ margin: "0 0 6px", fontSize: 13, color: "#f59e0b" }}>Open Option Positions ({open.length})</h4>
          <div className="stocks-table-wrapper">
            <table className="stocks-table" style={{ fontSize: 11 }}>
              <thead><tr><th>Parent</th><th>Conf</th><th>Dir</th><th>Index</th><th>Option</th><th>Strike</th><th>Qty</th><th>Entry ₹</th><th>Capital ₹</th><th>Entry Time</th></tr></thead>
              <tbody>
                {open.map(t => (
                  <tr key={t.parent_id} className="trade-open-row">
                    <td><span style={{ padding: "1px 5px", borderRadius: 3, fontSize: 9, fontWeight: 700, background: "#60a5fa22", color: "#60a5fa", border: "1px solid #60a5fa" }}>{t.parent_strategy}</span></td>
                    <td><ConfBadge t={t} /></td>
                    <td><span className={`signal-badge ${t.direction === "BULLISH" ? "badge-long" : "badge-short"}`}>{t.direction === "BULLISH" ? "BULL" : "BEAR"}</span></td>
                    <td>{t.index}</td>
                    <td><strong style={{ color: t.opt_type === "CE" ? "#34d399" : "#f87171" }}>{t.opt_type}</strong> <span style={{ fontSize: 9, color: "var(--grey-text2)" }}>{t.symbol}</span></td>
                    <td>{t.strike}</td>
                    <td>{t.qty}</td>
                    <td><strong>{t.entry_premium}</strong></td>
                    <td>{t.capital}</td>
                    <td style={{ fontSize: 10 }}>{fmtTime(t.entry_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ padding: "8px 12px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h4 style={{ margin: 0, fontSize: 13, color: "var(--grey-text)" }}>Closed Option Trades ({closed.length})</h4>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-export" onClick={() => exportCSV(trades)}>Export CSV</button>
            {closed.length > 0 && <button className="btn btn-sm btn-danger" onClick={clear}>Clear</button>}
          </div>
        </div>
        {closed.length > 0 && (
          <div className="stocks-table-wrapper">
            <table className="stocks-table" style={{ fontSize: 11 }}>
              <thead><tr><th>Parent</th><th>Conf</th><th>Dir</th><th>Option</th><th>Strike</th><th>Qty</th><th>Entry ₹</th><th>Exit ₹</th><th>P&L ₹</th><th>Result</th><th>Exit Reason</th><th>Spot pts</th><th>Exit Time</th></tr></thead>
              <tbody>
                {closed.map(t => (
                  <tr key={t.parent_id} className={t.result === "WIN" ? "trade-win" : "trade-loss"}>
                    <td><span style={{ padding: "1px 5px", borderRadius: 3, fontSize: 9, fontWeight: 700, background: "#60a5fa22", color: "#60a5fa", border: "1px solid #60a5fa" }}>{t.parent_strategy}</span></td>
                    <td><ConfBadge t={t} /></td>
                    <td><span className={`signal-badge ${t.direction === "BULLISH" ? "badge-long" : "badge-short"}`}>{t.direction === "BULLISH" ? "BULL" : "BEAR"}</span></td>
                    <td><strong style={{ color: t.opt_type === "CE" ? "#34d399" : "#f87171" }}>{t.opt_type}</strong></td>
                    <td>{t.strike}</td>
                    <td>{t.qty}</td>
                    <td>{t.entry_premium}</td>
                    <td>{t.exit_premium}</td>
                    <td className={t.pnl >= 0 ? "positive" : "negative"}>{t.pnl > 0 ? "+" : ""}₹{t.pnl}</td>
                    <td><span style={{ color: t.result === "WIN" ? "#34d399" : "#f87171", fontWeight: 700 }}>{t.result || "?"}</span></td>
                    <td style={{ fontSize: 10 }}>{t.exit_reason}</td>
                    <td style={{ fontSize: 10, color: t.parent_pnl_pts >= 0 ? "#34d399" : "#f87171" }}>{t.parent_pnl_pts != null ? (t.parent_pnl_pts > 0 ? "+" : "") + t.parent_pnl_pts : "-"}</td>
                    <td style={{ fontSize: 10 }}>{fmtTime(t.exit_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!status?.active && closed.length === 0 && open.length === 0 && (
        <div style={{ padding: 24, textAlign: "center", color: "var(--btn-border)" }}>
          <p>Click <strong>Start Executor</strong> to paper-trade options off the Strategy Scanner's S1/S2 signals.</p>
          <p style={{ fontSize: 12 }}>It buys an {sm} CE (bullish) / PE (bearish) when a signal fires and exits when that S1/S2 trade exits. The Strategy Scanner is read-only and unchanged. Start the Strategy Scanner too for signals to flow.</p>
        </div>
      )}
    </div>
  );
}
